import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { DateFormatter } from '@repo/toolkit/date'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import { Column } from '@repo/ui/data-table'
import { FileContractIcon } from '@repo/ui/icons'
import { endOfDay, endOfYear, isBefore, startOfYear } from 'date-fns'
import { useMemo, useRef, useState } from 'react'

import { API } from '../../../../api/apis.config'
import { Paths } from '../../../../lib/constants/path'
import { useFilters } from '../../../../lib/hooks/use-filters'
import { AffectedStaffStatusEnum } from '../../../../lib/types/enums/affected-staff/affected-staff-status.enum'
import { currencyEnumMapping } from '../../../../lib/types/enums/common/currency.enum'
import {
  AffectedStaffJourney,
  AffectedStaffMap
} from '../../../../lib/types/models/affectedStaff/affected-staff'
import { AffectedStaffFilters } from '../../../../lib/types/models/affectedStaff/affected-staff-input'
import { getTranslatedName } from '../../../../lib/utils/functions/translation.functions'
import CountryFlag from '../../../elements/country-flag'
import DynamicDataTable, { DynamicTableRef } from '../../../elements/dynamic-table'
import { QualificationBadge } from '../../../elements/qualification-badge'
import { createAffectedStaffSeniorityColumn } from '../../affected-staffs/affected-staff-seniority-column'
import { formatReviewNumber } from '../../ols/centers/center-tab-views/center-end-of-stay-appreciations/review-rating-pill'

type Props = {
  staffId?: number
}

const StaffJourneysSection: React.FC<Props> = ({ staffId }) => {
  const tableRef = useRef<DynamicTableRef<AffectedStaffJourney>>(null)
  const [mode, setMode] = useState<'initial' | 'extended'>('initial')

  const initialFilter: AffectedStaffFilters = useMemo(
    () => ({
      statuses: [AffectedStaffStatusEnum.PENDING, AffectedStaffStatusEnum.AFFECTED],
      departureDateRange: {
        startDate: DateFormatter.toDateFormatted(startOfYear(new Date())),
        endDate: DateFormatter.toDateFormatted(endOfYear(new Date()))
      },
      staffId
    }),
    [staffId]
  )

  const extendedFilter: AffectedStaffFilters = useMemo(
    () => ({
      statuses: [
        AffectedStaffStatusEnum.PENDING,
        AffectedStaffStatusEnum.AFFECTED,
        AffectedStaffStatusEnum.CANCELLED
      ],
      staffId
    }),
    [staffId]
  )

  const { selectedFilter, onSelectedFilterChange } = useFilters<AffectedStaffFilters>({
    initialFilters: initialFilter,
    options: { allowSearchParam: false }
  })

  const columns: Column<AffectedStaffJourney>[] = [
    {
      accessorKey: 'staffRequest.centers.name',
      header: 'Centre',
      cell: ({ row }) => {
        const endDate = row.original.staffRequest.endDate
        if (!endDate) return '-'
        const centers = row.original.staffRequest.centers.map((center) => center.name).join(', ')
        const passed = isBefore(endOfDay(new Date(endDate)), new Date())
        const status = row.original.status
        if (status === AffectedStaffStatusEnum.CANCELLED)
          return (
            <div className="flex flex-col gap-1">
              <span>{centers}</span>
              <Badge variant="danger" text="Annulé" />
            </div>
          )

        if (passed)
          return (
            <div className="flex flex-col gap-1">
              <span>{centers}</span>
              <Badge variant="success" text="Passé" />
            </div>
          )

        if (status === AffectedStaffStatusEnum.PENDING) return centers
        return <Badge variant="orange" text={centers} />
      }
    },
    {
      accessorKey: 'staffRequest.qualification',
      header: 'Qualification',
      enableSorting: true,
      cell: ({ row }) => (
        <QualificationBadge qualification={row.original.staffRequest.qualification} />
      )
    },
    {
      accessorKey: 'staffRequest.country.name',
      header: 'Pays',
      enableSorting: true,
      cell: ({ row }) => <CountryFlag code={row.original.staffRequest.country?.code} />
    },
    {
      accessorKey: 'staffRequest.formulas.title',
      header: 'Formules',
      cell: ({ row }) => {
        const formulas = row.original.staffRequest.formulas
        if (formulas.length === 0) return '-'
        return formulas.map((formula) => getTranslatedName(formula)).join(', ')
      }
    },
    {
      accessorKey: 'staffRequest.startDate',
      header: 'Date de séjour',
      enableSorting: true,
      cell: ({ row }) => {
        const startDate = row.original.staffRequest.startDate
        const endDate = row.original.staffRequest.endDate
        if (!startDate || !endDate) return '-'
        return (
          <Badge
            text={`${DateFormatter.DDMMYYYY(startDate)} - ${DateFormatter.DDMMYYYY(endDate)}`}
          />
        )
      }
    },
    {
      accessorKey: 'staffRequest.duration',
      header: 'Durée (jours)',
      cell: ({ row }) => row.original.staffRequest.duration ?? '-'
    },
    createAffectedStaffSeniorityColumn<AffectedStaffJourney>(),
    {
      accessorKey: 'totalSalary',
      header: 'Rémunération',
      enableSorting: true,
      cell: ({ row }) =>
        row.original.totalSalary
          ? `${row.original.totalSalary} ${currencyEnumMapping[row.original.currency].symbol}`
          : '-'
    },
    {
      accessorKey: 'travelExpenses',
      header: 'Frais',
      cell: ({ row }) => {
        const total = row.original.travelExpenses
          .map((expense) => expense.validatedAmount)
          .reduce((a, b) => (a ?? 0) + (b ?? 0), 0)
        return `${total} ${currencyEnumMapping[row.original.travelExpensesCurrency].symbol}`
      }
    },
    {
      accessorKey: 'evaluation',
      header: 'Éval',
      cell: ({ row }) =>
        row.original.evaluation !== undefined && row.original.evaluation !== null
          ? `${formatReviewNumber(row.original.evaluation)}/${row.original.evaluationScaleMax}`
          : '-'
    },
    {
      accessorKey: 'rating',
      enableSorting: true,
      header: 'Note',
      cell: ({ row }) =>
        row.original.rating !== undefined && row.original.rating !== null
          ? row.original.rating
          : '-'
    },
    {
      accessorKey: 'contract.contractSignedAt',
      header: 'Contrat',
      enableSorting: true,
      cell: ({ row }) => {
        const signed = !!row.original.contract?.contractSignedAt
        return (
          <Badge
            variant={signed ? 'success' : 'danger'}
            icon={
              <FileContractIcon
                color={signed ? tailwindColors.green.DEFAULT : tailwindColors.red.DEFAULT}
              />
            }
          />
        )
      }
    }
  ]

  if (!staffId) return <div>Aucun séjour</div>
  return (
    <div>
      <Button
        label={mode === 'initial' ? 'Voir tous les séjours' : 'Masquer les séjours'}
        size="medium"
        type="button"
        variant={mode === 'initial' ? 'primary' : 'ghost'}
        onClick={() => {
          if (mode === 'initial') {
            onSelectedFilterChange(extendedFilter)
            setMode('extended')
          } else {
            onSelectedFilterChange(initialFilter)
            setMode('initial')
          }
        }}
      />
      <div className="h-4" />
      <DynamicDataTable<AffectedStaffJourney, AffectedStaffFilters, never, keyof AffectedStaffMap>
        ref={tableRef}
        columns={columns}
        specialRows={[
          {
            condition: (row) => {
              const endDate = row.staffRequest.endDate
              if (!endDate) return false
              return (
                isBefore(endOfDay(new Date(endDate)), new Date()) &&
                row.status !== AffectedStaffStatusEnum.CANCELLED
              )
            },
            color: tailwindColors.green.light.hover
          },
          {
            condition: (row) => row.status === AffectedStaffStatusEnum.CANCELLED,
            color: tailwindColors.red.light.hover
          }
        ]}
        endpoint={API.affectedStaffs.getAll.url}
        onRowClick={(row) => {
          window.open(
            Paths.AFFECTED_STAFFS_VIEW_PAGE.replace(':id', row.id.toString()),
            '_blank',
            'noopener,noreferrer'
          )
        }}
        version={'JOURNEY'}
        headerType="none"
        filters={selectedFilter}
        sorting={{ id: 'staffRequest_startDate', desc: true }}
        paginated={false}
      />
    </div>
  )
}

export default StaffJourneysSection
